import { memo, useMemo, useReducer } from 'react';

import dayjs from 'dayjs';
import { SquarePen, Trash2 } from 'lucide-react';
import { isNonNullish, isNullish, omit, pick } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { EntryRewardFormDialog } from '../../entryRewardFormDialog/EntryRewardFormDialog';
import { IEntryRewardForm } from '../../form';

import css from './EntryRewardsTableActionsColumn.module.css';

import { entryRewardService } from 'v2/api/entryRewardService';
import { rewardsService } from 'v2/api/rewardsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { EntryRewardStatus } from 'v2/constants';
import { Nullable } from 'v2/types';
import { EntryRewardModel } from 'v2/types/RewardModel';
import { handleHttpError } from 'v2/utils';

interface EntryRewardCardProps {
  entryReward: EntryRewardModel;
  onRevalidate: VoidFunction;
}

export const EntryRewardsTableActionsColumn = memo<EntryRewardCardProps>(({ entryReward, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data: reward } = useSWR(isOpen && ['getRewardById', entryReward.rewardId], rewardsService.getRewardById);

  const { trigger: triggerUpdateEntryReward, isMutating: isEntryRewardMutating } = useSWRMutation(
    ['updateEntryReward', entryReward.id],
    entryRewardService.updateEntryReward,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The entry reward has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerUpdateReward, isMutating: isRewardMutating } = useSWRMutation(
    ['updateReward', entryReward.rewardId],
    rewardsService.updateReward,
    { onError: handleHttpError }
  );

  const { trigger: triggerDeleteEntryReward } = useSWRMutation(
    ['deleteEntryReward', entryReward.id],
    entryRewardService.deleteEntryReward,
    {
      onSuccess: () => {
        triggerDeleteReward();
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDeleteReward } = useSWRMutation(
    ['deleteReward', entryReward.rewardId],
    rewardsService.deleteReward,
    { onError: handleHttpError }
  );

  const initialValues: Nullable<IEntryRewardForm> = useMemo(() => {
    if (isNullish(reward)) return null;

    return {
      ...pick(entryReward, ['title', 'description', 'imageId']),
      activationDate: dayjs(entryReward.activationDate),
      expirationDate: dayjs(entryReward.expirationDate),
      ...omit(reward, ['id']),
    };
  }, [entryReward, reward]);

  const handleSubmit = (values: IEntryRewardForm, resetForm: VoidFunction) => {
    triggerUpdateReward({ type: values.type, value: values.value })
      .then((r) => {
        return triggerUpdateEntryReward({
          ...omit(values, ['type', 'value']),
          activationDate: values.activationDate.format(),
          expirationDate: values.expirationDate?.format() ?? '',
          rewardId: r.id,
        });
      })
      .then(() => {
        resetForm();
      });
  };

  return (
    <div className={css.entryRewardsTableActionsColumn}>
      <Tooltip title="Edit" placement="top">
        <IconButton onClick={setOpen} color="warning" disabled={entryReward.status === EntryRewardStatus.EXPIRED}>
          <SquarePen width={22} height={22} />
        </IconButton>
      </Tooltip>

      <Popconfirm
        message="Are you sure that you want to delete this entry reward?"
        onConfirm={triggerDeleteEntryReward}
      >
        <Tooltip title="Delete" placement="top">
          <IconButton color="error">
            <Trash2 width={22} height={22} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      {isNonNullish(initialValues) && (
        <EntryRewardFormDialog
          title="Edit Entry Reward"
          isOpen={isOpen}
          onClose={setOpen}
          initialValues={initialValues}
          isSubmitting={isEntryRewardMutating || isRewardMutating}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
});

EntryRewardsTableActionsColumn.displayName = 'EntryRewardCard';
